import React, { useState, useEffect } from "react";
import { BellOff, BellRing, BellPlus, BellMinus, Loader2, Check } from "lucide-react";
import { cn } from "util/cn";
import api from "config/axiosConfig";
import { useUser } from "util/UserContext";
import TooltipWrapper from "./TooltipWrapper";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./DropdownMenu";
import { Drawer, DrawerContent, DrawerTrigger } from "./Drawer";
import { apiRoutes } from "util/apiRoutes";

export interface EntitySubscriptionProps {
  entityId: number | string;
  entityType: "issue" | "impact" | "solution" | "action" | "feedback" | "collection" | "user" | "team" | "crosspost" | "comment" | "teabate";
  variant?: "default" | "mini";
  className?: string;
  showLabel?: boolean;
  disabled?: boolean;
  onSubscriptionChange?: (subscribed: boolean, level: number) => void;
}

interface SubscriptionData {
  id: number;
  properties: {
    value: number;
    privacy: string;
  };
  is_active: boolean;
}

/**
 * EntitySubscription component allows users to subscribe to entities for notifications.
 * Supports two variants: default (with label) and mini (icon only).
 * Uses properties.value to store subscription level: 0 (none) or 1 (all).
 */
const EntitySubscription: React.FC<EntitySubscriptionProps> = ({
  entityId,
  entityType,
  variant = "default",
  className,
  showLabel = true,
  disabled = false,
  onSubscriptionChange,
}) => {
  const { currentUser, setShowAuthModal, userIsMobile } = useUser();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isMini = variant === "mini";
  const hasSubscription = subscription?.is_active;
  const subscriptionLevel = subscription?.properties?.value;

  /**
   * Opens the subscription management UI appropriate for the device.
   * - Mobile: opens the Drawer
   * - Desktop: opens the Dropdown menu
   */
  const openSubscriptionManager = () => {
    if (userIsMobile) {
      setDrawerOpen(true);
    } else {
      setMenuOpen(true);
    }
  };

  // Load current subscription status
  useEffect(() => {
    const loadSubscription = async () => {
      if (!currentUser?.id) {
        setIsInitialLoading(false);
        return;
      }

      try {
        const response = await api.get(apiRoutes.user.subscriptionStatus(entityType, entityId));
        setSubscription(response.data);
      } catch (error) {
        console.error("Error loading subscription:", error);
        setSubscription(null);
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadSubscription();
  }, [currentUser?.id, entityType, entityId]);

  const handleSubscriptionChange = async (level: number | null) => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    if (disabled || isLoading) return;

    setIsLoading(true);
    setMenuOpen(false);
    setDrawerOpen(false);

    try {
      if (level === null) {
        // Unsubscribe completely
        if (subscription?.id) {
          await api.delete(apiRoutes.user.subscriptionsByUserId(subscription.id));
          setSubscription(null);
          toast.success("Unsubscribed", {
            description: `You will no longer receive notifications about this ${entityType}`,
            icon: <BellMinus className="size-4" />,
          });
          onSubscriptionChange?.(false, -1);
        }
      } else {
        // Create or update subscription
        const response = await api.post(apiRoutes.user.subscriptions, {
          entityType,
          entityId: parseInt(entityId.toString(), 10),
          level,
          privacy: "public"
        });

        // Update the subscription state with the correct structure
        const newSubscription: SubscriptionData = {
          id: response.data.id || response.data.subscription?.id || subscription?.id || Date.now(),
          properties: {
            value: level,
            privacy: "public"
          },
          is_active: true
        };
        
        setSubscription(newSubscription);
        onSubscriptionChange?.(level !== 0, level);

        if (!hasSubscription) {
          // New subscription
          toast.success("Subscribed", {
            description: level === 1 
              ? `You'll receive all notifications about this ${entityType}`
              : `Notifications from this ${entityType} are muted`,
            icon: level === 1 ? <BellRing className="size-4" /> : <BellOff className="size-4" />,
            action: {
              label: "Modify",
              onClick: () => {
                openSubscriptionManager();
              }
            }
          });
        } else {
          // Updated subscription
          toast.success("Subscription updated", {
            description: level === 1 
              ? `You'll receive all notifications about this ${entityType}`
              : `Notifications about this ${entityType} are muted`,
            icon: level === 1 ? <BellRing className="size-4" /> : <BellOff className="size-4" />,
            action: {
              label: "Modify",
              onClick: () => {
                openSubscriptionManager();
              }
            }
          });
        }
      }
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast.error("Failed to update subscription", {
        description: "Please try again later",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickSubscribe = () => {
    if (!hasSubscription) {
      handleSubscriptionChange(1); // Subscribe with all notifications
    } else {
      // If already subscribed, open menu/drawer to manage
      if (userIsMobile) {
        setDrawerOpen(true);
      } else {
        setMenuOpen(true);
      }
    }
  };

  // Don't render if user is not authenticated
  if (!currentUser) {
    return null;
  }

  // Show loading state during initial load
  if (isInitialLoading) {
    return (
      <div className={cn(
        "inline-flex items-center gap-2",
        isMini ? "size-4" : "h-5",
        className
      )}>
        <div className={cn(
          "animate-pulse rounded-full",
          isMini ? "size-5" : "size-6"
        )} />
      </div>
    );
  }

  // Get the appropriate icon based on subscription state
  const getIcon = (size: string = "size-4") => {
    if (isLoading) {
      return <Loader2 className={cn(size, "animate-spin")} />;
    }
    if (!hasSubscription) {
      return <BellPlus className={cn(size)} />;
    }
    if (subscriptionLevel === 0) {
      return <BellOff className={cn(size, "stroke-current")} />;
    }
    // BellRing for "All" notifications - filled style
    return <BellRing className={cn(size, "fill-current")} />;
  };

  const getTooltipText = () => {
    if (!hasSubscription) {
      return `Subscribe to ${entityType}`;
    }
    if (subscriptionLevel === 0) {
      return `Notifications muted - Click to manage`;
    }
    return `Receiving all notifications - Click to manage`;
  };

  const getButtonClassName = () => {
    return cn(
      "h-9 rounded-full px-2 text-eoe-gray dark:text-eoe-dark-icon-stroke dark:hover:text-eoe-dark-icon-stroke-hover",
      "flex justify-center items-center",
      "hover:bg-gray-300 dark:hover:bg-eoe-dark-icon-hover transition-colors",
      isMini && "size-6 p-0",
      !isMini && "px-3 gap-1.5 bg-gray-200 dark:bg-eoe-dark-icon ",
      hasSubscription && subscriptionLevel === 1 && "text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke",
      hasSubscription && subscriptionLevel === 0 && "text-eoe-gray dark:text-eoe-dark-icon-stroke",
      !hasSubscription && "text-eoe-gray dark:text-eoe-dark-icon-stroke",
      className
    );
  };

  // Dropdown menu options for desktop
  const DropdownOptions = () => (
    <>
      <DropdownMenuItem 
        onClick={() => handleSubscriptionChange(1)}
        className="flex items-center gap-3 py-2.5 cursor-pointer"
      >
        <BellRing className="size-4" />
        <div className="flex-1">
          <div className="font-medium">All notifications</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Get notified about all activity
          </div>
        </div>
        {subscriptionLevel === 1 && (
          <Check className="size-4 eoe-icon-stroke" />
        )}
      </DropdownMenuItem>

      <DropdownMenuItem 
        onClick={() => handleSubscriptionChange(0)}
        className="flex items-center gap-3 py-2.5 cursor-pointer"
      >
        <BellOff className="size-4" />
        <div className="flex-1">
          <div className="font-medium">Mute notifications</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Stay subscribed but mute alerts
          </div>
        </div>
        {subscriptionLevel === 0 && (
          <Check className="size-4" />
        )}
      </DropdownMenuItem>

      <DropdownMenuSeparator />

      <DropdownMenuItem 
        onClick={() => handleSubscriptionChange(null)}
        className="flex items-center gap-3 py-2.5 cursor-pointer text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
      >
        <BellMinus className="size-4" />
        <div className="font-medium">Unsubscribe</div>
      </DropdownMenuItem>
    </>
  );

  // Drawer options for mobile
  const DrawerOptions = () => (
    <div className="flex flex-col gap-2">
      <button
        onClick={() => handleSubscriptionChange(1)}
        className="flex items-center gap-3 py-3 px-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors w-full text-left"
      >
        <BellRing className="size-5" />
        <div className="flex-1">
          <div className="font-medium">All notifications</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Get notified about all activity
          </div>
        </div>
        {subscriptionLevel === 1 && (
          <Check className="size-5" />
        )}
      </button>

      <button
        onClick={() => handleSubscriptionChange(0)}
        className="flex items-center gap-3 py-3 px-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors w-full text-left"
      >
        <BellOff className="size-5" />
        <div className="flex-1">
          <div className="font-medium">Mute notifications</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Stay subscribed but mute alerts
          </div>
        </div>
        {subscriptionLevel === 0 && (
          <Check className="size-5" />
        )}
      </button>

      <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />

      <button
        onClick={() => handleSubscriptionChange(null)}
        className="flex items-center gap-3 py-3 px-4 rounded-lg hover:bg-red-50 dark:hover:bg-red-950 transition-colors w-full text-left text-red-600 dark:text-red-400"
      >
        <BellMinus className="size-5" />
        <div className="font-medium">Unsubscribe</div>
      </button>
    </div>
  );

  const buttonContent = (
    <>
      {getIcon()}
      {!isMini && showLabel && (
        <span className="text-sm ml-1 font-semibold">
          {!hasSubscription ? "Subscribe" : subscriptionLevel === 0 ? "Muted" : "Subscribed"}
        </span>
      )}
    </>
  );

  const SubscriptionButton = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement>
  >((props, ref) => (
    <button
      ref={ref}
      {...props}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        handleQuickSubscribe();
      }}
      disabled={disabled || isLoading}
      className={getButtonClassName()}
      title={getTooltipText()}
      type="button"
    >
      {buttonContent}
    </button>
  ));
  SubscriptionButton.displayName = "SubscriptionButton";

  // Wrap everything to prevent card click propagation
  const content = (
    <>
      {/* If not subscribed, just show the button */}
      {!hasSubscription ? (
        isMini ? (
          <TooltipWrapper title={getTooltipText()} arrow>
            <SubscriptionButton />
          </TooltipWrapper>
        ) : (
          <SubscriptionButton />
        )
      ) : userIsMobile ? (
        // Mobile: Use Drawer
        <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
          <DrawerTrigger asChild>
            <SubscriptionButton />
          </DrawerTrigger>
          <DrawerContent title="Notifications">
            <div className="p-4">
              <DrawerOptions />
            </div>
          </DrawerContent>
        </Drawer>
      ) : (
        // Desktop: Use DropdownMenu
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
          <DropdownMenuTrigger asChild>
            <SubscriptionButton />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownOptions />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );

  // Return with click propagation prevention wrapper
  return (
    <div 
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      className="inline-flex"
    >
      {content}
    </div>
  );
};

export default EntitySubscription;
